import { Component, Input, OnInit, signal, inject, OnChanges, SimpleChanges } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { ContractRegistryService } from '../../services/contract-registry.service';
import { SupplierRegistryService } from '../../services/supplier-registry.service';
import { ResponsibleRegistryService } from '../../services/responsible-registry.service';
import { SubResponsibleRegistryService } from '../../services/sub-responsible-registry.service';
import {
  FormDropdownService,
  FormDropdownItem,
} from '../../../core/services/form-dropdown.service';
import { ConfirmationDialogService } from '../../../core/services/confirmation-dialog.service';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';
import { ColumnConfig } from '../dashboard/table-config';
import { MessageService, MenuItem, ConfirmationService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { forkJoin, Observable } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { UserSessionService } from '../../../core/services/mock-auth.service';
import { AppRole } from '../../../core/models/permissions.model';
import {
  RegistrationStepService,
  RegistrationStep,
} from '../../../core/services/registration-step.service';

import { NavigationContextService } from '../../../core/services/navigation-context.service';

@Component({
  selector: 'app-contracts-registry',
  standalone: true,
  imports: [
    CommonModule,
    RegistryTableComponent,
    Toast,
    BreadcrumbModule,
    ButtonModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService],
  templateUrl: './contracts-registry.component.html',
  styleUrls: ['./contracts-registry.component.scss'],
})
export class ContractsRegistryComponent implements OnInit, OnChanges {
  private router = inject(Router);
  private contractService = inject(ContractRegistryService);
  private supplierService = inject(SupplierRegistryService);
  private responsibleService = inject(ResponsibleRegistryService);
  private subResponsibleService = inject(SubResponsibleRegistryService);
  private formDropdownService = inject(FormDropdownService);
  private messageService = inject(MessageService);
  private confirmDialogService = inject(ConfirmationDialogService);
  private authService = inject(AuthService);
  private mockAuthService = inject(UserSessionService);
  private registrationStepService = inject(RegistrationStepService);
  private navContext = inject(NavigationContextService);
  private titleService = inject(Title);
  private route = inject(ActivatedRoute);

  /**
   * ID del fornitore per filtrare i contratti.
   * Se non specificato, mostra tutti i contratti.
   */
  @Input() supplierId?: string;

  /**
   * ID del responsible per filtrare i contratti.
   */
  @Input() responsibleId?: string;

  /**
   * ID del sub-responsible per filtrare i contratti.
   */
  @Input() subResponsibleId?: string;

  /**
   * Breadcrumb del parent component da propagare al dettaglio contratto
   */
  @Input() parentBreadcrumb?: MenuItem[];

  /**
   * Mostra il breadcrumb (solo in modalità standalone)
   */
  @Input() showBreadcrumb: boolean = false;

  /**
   * Mostra il pulsante di esportazione
   */
  @Input() showExport: boolean = true;

  /**
   * Mostra il pulsante di pulizia filtri
   */
  @Input() showClearFilters: boolean = true;

  @Input() professionalId?: string;

  // State
  contractsData = signal<any[]>([]);
  isLoading = signal<boolean>(false);
  breadcrumbItems = signal<MenuItem[]>([]);
  emptyTitle = signal<string>('Nessun elemento registrato');
  emptyMessage = signal<string>('Non ci sono elementi da visualizzare in questo momento.');
  vrfUseCaseOptions = signal<FormDropdownItem[]>([]);
  serviceTypeOptions = signal<FormDropdownItem[]>([]);
  trusteeCategoryOptions = signal<{ label: string; value: string }[]>([]);
  pageTitle = signal<string>('Registro Contratti');
  pageSubtitle = signal<string>('Gestione completa di tutti i contratti aziendali');

  // Column configuration for Supplier contracts
  supplierColumns: ColumnConfig[] = [
    {
      field: 'contractId',
      title: 'ID Contratto',
      type: 'text',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca ID',
    },
    {
      field: 'supplierName',
      title: 'Ragione Sociale',
      type: 'text',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca ragione sociale',
    },
    {
      field: 'serviceType',
      title: 'Tipologia Servizio',
      type: 'text',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca tipologia',
    },
    {
      field: 'contractObject',
      title: 'Oggetto del Contratto',
      type: 'truncated',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca oggetto',
      truncateAt: 60,
    },
    { field: 'startDate', title: 'Data Inizio', type: 'text', sortable: true, filterType: 'date' },
    { field: 'endDate', title: 'Data Fine', type: 'text', sortable: true, filterType: 'date' },
    {
      field: 'status',
      title: 'Stato',
      type: 'tag',
      sortable: true,
      filterType: 'multiselect',
      filterPlaceholder: 'Filtra stato',
      filterOptions: [
        { label: 'Bozza', value: 'DRAFT' },
        { label: 'Da attivare', value: 'GENERATED' },
        { label: 'Attivo', value: 'ACTIVE' },
        { label: 'In Rinnovo', value: 'IN_RENEWAL' },
        { label: 'In Scadenza', value: 'EXPIRING' },
        { label: 'Scaduto', value: 'EXPIRED' },
        { label: 'Cessato', value: 'CEASED' },
      ],
    },
    { title: 'Azioni', type: 'action', filterType: 'none' },
  ];

  // Column configuration for Professional contracts (Fiduciari)
  professionalColumns: ColumnConfig[] = [
    {
      field: 'contractId',
      title: 'ID',
      type: 'text',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca ID',
    },
    {
      field: 'professionalBusinessName',
      title: 'Ragione Sociale/Nominativo',
      type: 'text',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca Nome/Ragione Sociale',
    },
    {
      field: 'professionalVatNumber',
      title: 'Partita IVA',
      type: 'text',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca Partita IVA',
    },
    {
      field: 'professionalCategory',
      title: 'Categoria Fiduciario',
      type: 'tag',
      sortable: true,
      filterType: 'select',
      filterPlaceholder: 'Filtra categoria',
      filterOptions: [], // Popolato dinamicamente dall'ngOnInit
    },
    {
      field: 'status',
      title: 'Stato',
      type: 'tag',
      sortable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Attivo', value: 'ACTIVE' },
        { label: 'In Rinnovo', value: 'IN_RENEWAL' },
        { label: 'Cessato', value: 'CEASED' },
      ],
    },
    { 
      field: 'startDate', 
      title: 'Data di inizio', 
      type: 'text', 
      sortable: true, 
      filterType: 'date' 
    },
    { 
      field: 'endDate', 
      title: 'Data di fine', 
      type: 'text', 
      sortable: true, 
      filterType: 'date' 
    },
    {
      field: 'buyer',
      title: 'Buyer',
      type: 'text',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca Buyer',
    },
    { title: 'Azioni', type: 'action', filterType: 'none' },
  ];

  // Dynamic columns based on context
  columns: ColumnConfig[] = [];
  /** Filtri iniziali da applicare visivamente sulla tabella (es. da queryParams dashboard) */
  initialFilters = signal<Record<string, any>>({});

  ngOnInit(): void {
    this.titleService.setTitle('Registro Contratti - PalEasy');
    
    // Leggi queryParams da navigazione dashboard
    const params = this.route.snapshot.queryParams;
    if (params['category'] || params['status']) {
      const filters: Record<string, any> = {};
      if (params['category']) filters['professionalCategory'] = params['category'];
      if (params['status']) filters['status'] = params['status'];
      this.initialFilters.set(filters);
    }
    
    // Inizializza breadcrumb per modalità standalone
    this.breadcrumbItems.set([{ label: 'Registro Contratti' }]);

    this.resolveProfessionalContext();

    // Usa NavigationContextService per determinare il contesto (stessa logica delle API)
    const isSupplierContext = this.navContext.isSupplierContext();
    
    console.log('🎯 Navigation Context - isSupplierContext:', isSupplierContext);

    // Imposta le colonne e i titoli in base al contesto
    if (!isSupplierContext) {
      // Professional context
      this.columns = this.professionalColumns;
      this.pageTitle.set('Registro Contratti Fiduciari');
      this.pageSubtitle.set('Gestione completa di tutti i contratti con fiduciari professionali');
      this.breadcrumbItems.set([{ label: 'Registro Contratti Fiduciari' }]);
    } else {
      // Supplier context
      this.columns = this.supplierColumns;
      this.pageTitle.set('Registro Contratti Fornitori');
      this.pageSubtitle.set('Gestione completa di tutti i contratti con fornitori');
      this.breadcrumbItems.set([{ label: 'Registro Contratti Fornitori' }]);
    }

    // Imposta messaggio empty state in base al contesto
    if (this.isProfessionalUser()) {
      this.emptyTitle.set('Nessun contratto associato');
      this.emptyMessage.set('Questo fiduciario non ha contratti associati.');
    } else if (this.supplierId) {
      this.emptyTitle.set('Nessun contratto associato');
      this.emptyMessage.set('Questo fornitore non ha contratti associati.');
    } else if (this.responsibleId) {
      this.emptyTitle.set('Nessun contratto associato');
      this.emptyMessage.set('Questo responsabile non ha contratti associati.');
    } else if (this.subResponsibleId) {
      this.emptyTitle.set('Nessun contratto associato');
      this.emptyMessage.set('Questo sub-responsabile non ha contratti associati.');
    }

    this.loadDropdownOptionsAndContracts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Ricarica i contratti se cambia il supplierId, responsibleId o subResponsibleId
    if (
      (changes['supplierId'] && !changes['supplierId'].firstChange) ||
      (changes['responsibleId'] && !changes['responsibleId'].firstChange) ||
      (changes['subResponsibleId'] && !changes['subResponsibleId'].firstChange) ||
      (changes['professionalId'] && !changes['professionalId'].firstChange)
    ) {
      this.resolveProfessionalContext();
      
      // Usa NavigationContextService per determinare il contesto
      const isSupplierContext = this.navContext.isSupplierContext();
      
      // Aggiorna le colonne e i titoli in base al nuovo contesto
      if (!isSupplierContext) {
        this.columns = this.professionalColumns;
        this.pageTitle.set('Registro Contratti Fiduciari');
        this.pageSubtitle.set('Gestione completa di tutti i contratti con fiduciari professionali');
        this.breadcrumbItems.set([{ label: 'Registro Contratti Fiduciari' }]);
      } else {
        this.columns = this.supplierColumns;
        this.pageTitle.set('Registro Contratti Fornitori');
        this.pageSubtitle.set('Gestione completa di tutti i contratti con fornitori');
        this.breadcrumbItems.set([{ label: 'Registro Contratti Fornitori' }]);
      }
      
      this.loadContracts();
    }
  }

  private isProfessionalUser(): boolean {
    const mockRoles = this.mockAuthService.getCurrentUser()?.roles ?? [];
    const authRoles = this.authService.getCurrentUser()?.roles ?? [];

    return [...mockRoles, ...authRoles].includes(AppRole.PROFESSIONAL);
  }

  private resolveProfessionalContext(): void {
    if (!this.isProfessionalUser()) {
      return;
    }

    this.professionalId =
      this.professionalId ||
      this.mockAuthService.getCurrentUser()?.professionalId ||
      this.authService.getCurrentUser()?.professionalId ||
      this.mockAuthService.getDemoProfessionalId() ||
      undefined;
  }

  private loadDropdownOptionsAndContracts(): void {
    // Carica prima tutte le opzioni dropdown, poi i contratti
    forkJoin({
      vrfUseCase: this.formDropdownService.getFormDropdownValues('CONTRACT_FORM', 'VRF_USE_CASE'),
      serviceType: this.formDropdownService.getFormDropdownValues('CONTRACT_FORM', 'SERVICE_TYPE'),
      trusteeCategory: this.formDropdownService.getFormDropdownValues('TRUSTEE_CATEGORY', 'TRUSTEE_CATEGORY'),
    }).subscribe({
      next: (responses) => {
        if (responses.vrfUseCase?.status && responses.vrfUseCase?.data) {
          this.vrfUseCaseOptions.set(responses.vrfUseCase.data);
        }
        if (responses.serviceType?.status && responses.serviceType?.data) {
          this.serviceTypeOptions.set(responses.serviceType.data);
        }
        if (responses.trusteeCategory?.status && responses.trusteeCategory?.data) {
          // Popola le opzioni categoria fiduciario
          const categoryOptions = responses.trusteeCategory.data.map(item => ({
            label: item.valueDescription,
            value: item.valueKey,
          }));
          this.trusteeCategoryOptions.set(categoryOptions);
          
          // Aggiorna dinamicamente le filterOptions della colonna professionalCategory
          const categoryColumn = this.professionalColumns.find(col => col.field === 'professionalCategory');
          if (categoryColumn) {
            categoryColumn.filterOptions = categoryOptions;
          }
        }
        // Carica i contratti solo dopo aver caricato le opzioni
        this.loadContracts();
      },
      error: (err) => {
        console.error('Error loading dropdown options:', err);
        // Carica comunque i contratti anche in caso di errore
        this.loadContracts();
      },
    });
  }

  private loadContracts(): void {
    this.isLoading.set(true);

    let request: Observable<any>;

    if (this.isProfessionalUser()) {
      this.resolveProfessionalContext();

      if (!this.professionalId) {
        console.warn('Professional user detected but no professionalId is available.');
        this.contractsData.set([]);
        this.isLoading.set(false);
        return;
      }

      request = this.contractService.getContractsByProfessional(this.professionalId!);
    } else if (this.supplierId) {
      request = this.contractService.getContractsBySupplier(this.supplierId);
    } else if (this.responsibleId) {
      request = this.contractService.getContractsByResponsible(this.responsibleId);
    } else if (this.subResponsibleId) {
      request = this.contractService.getContractsBySubResponsible(this.subResponsibleId);
    } else {
      request = this.contractService.getAllContracts();
    }

    request.subscribe({
      next: (response: any) => {
        if (response?.status && response?.data?.content) {
          console.log('📊 Raw contracts data:', response.data.content);
          
          // Usa metodo centralizzato del servizio per mappare i contratti
          const formattedData = this.contractService.mapContractsForDisplay(
            response.data.content,
            this.vrfUseCaseOptions(),
            this.serviceTypeOptions(),
          );
          
          console.log('✨ Formatted contracts data:', formattedData);
          console.log('📋 Current columns:', this.columns);
          
          this.contractsData.set(formattedData);
          
          // Determina automaticamente quale set di colonne usare basandosi sui dati caricati
          this.determineColumnsByContractType(response.data.content);
          
          console.log('🎯 Columns after determination:', this.columns);
        } else {
          console.error('Invalid contracts response format:', response);
          this.contractsData.set([]);
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading contracts:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore nel caricamento contratti',
          detail: 'Impossibile caricare i contratti. Riprova più tardi.',
          life: 5000,
        });
        this.contractsData.set([]);
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Determina automaticamente quale set di colonne usare basandosi sul NavigationContext
   * (usa lo stesso metodo delle API calls per consistenza)
   */
  private determineColumnsByContractType(contracts: any[]): void {
    if (!contracts || contracts.length === 0) {
      console.log('⚠️ No contracts to determine type');
      return;
    }

    // Usa NavigationContextService - stessa logica delle API calls
    const isSupplierContext = this.navContext.isSupplierContext();
    
    console.log('🎯 NavigationContext check:', {
      isSupplierContext,
      shouldUseProfessionalColumns: !isSupplierContext,
      contractsCount: contracts.length
    });

    if (!isSupplierContext) {
      console.log('✅ Using PROFESSIONAL columns (NavigationContext)');
      this.columns = this.professionalColumns;
      this.pageTitle.set('Registro Contratti Fiduciari');
      this.pageSubtitle.set('Gestione completa di tutti i contratti con fiduciari professionali');
      this.breadcrumbItems.set([{ label: 'Registro Contratti Fiduciari' }]);
    } else {
      console.log('✅ Using SUPPLIER columns (NavigationContext)');
      this.columns = this.supplierColumns;
      this.pageTitle.set('Registro Contratti Fornitori');
      this.pageSubtitle.set('Gestione completa di tutti i contratti con fornitori');
      this.breadcrumbItems.set([{ label: 'Registro Contratti Fornitori' }]);
    }
  }

  onDetailClick(rowData: any): void {
    const contractId = rowData?.id || rowData?.contractId;
    if (contractId) {
      // Costruisci lo state in base al contesto
      const navigationState: any = {
        from: this.parentBreadcrumb ? 'fornitore-detail' : 'contracts-registry',
      };

      // Se c'è un breadcrumb del parent, usalo; altrimenti usa quello di default
      if (this.parentBreadcrumb && this.parentBreadcrumb.length > 0) {
        navigationState.breadcrumb = this.parentBreadcrumb;
      } else {
        navigationState.breadcrumb = [
          { label: 'Registro Contratti', routerLink: `/axa/${this.navContext.getDomainPrefix()}-contracts-registry` },
        ];
      }

      this.router.navigate([`/axa/${this.navContext.getDomainPrefix()}-contracts-registry`, contractId], {
        state: navigationState,
      });
    } else {
      console.log('Contract detail clicked but no ID found:', rowData);
      this.messageService.add({
        severity: 'warn',
        summary: 'Dettaglio Contratto',
        detail: 'Impossibile aprire il dettaglio: ID contratto non trovato',
        life: 3000,
      });
    }
  }

  onEditClick(rowData: any): void {
    if (rowData?.contractId) {
      this.router.navigate([`/axa/${this.navContext.getDomainPrefix()}-contract-registration-wizard`, rowData.contractId]);
    }
  }

  onDeleteClick(rowData: any): void {
    if (rowData?.contractId) {
      this.confirmDialogService.confirmDelete(
        `Sei sicuro di voler eliminare il contratto ${rowData.contractId}? Questa azione non può essere annullata.`,
        () => {
          this.isLoading.set(true);
          this.contractService.deleteContract(rowData.contractId).subscribe({
            next: (response: any) => {
              if (response?.status) {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Contratto eliminato',
                  detail: `Il contratto ${rowData.contractId} è stato eliminato con successo`,
                  life: 3000,
                });
                // La delete lato pal-dmn-contracts NON riallinea lo step di registrazione del
                // fiduciario (microservizi disaccoppiati). Se il fiduciario era avanzato in fase
                // contrattuale lo riportiamo a CONTRACT_SENT/PENDING, così torna selezionabile per
                // un nuovo contratto e non resta "senza stato" nel Registro Fiduciari.
                const professionalId =
                  rowData?.professionalId ?? rowData?.generalInfo?.professionalId;
                if (professionalId && !this.isProfessionalUser()) {
                  this.restoreProfessionalContractStep(professionalId);
                }
                // Ricarica i contratti dopo l'eliminazione
                this.loadContracts();
              } else {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Errore',
                  detail: "Si è verificato un errore durante l'eliminazione del contratto",
                  life: 3000,
                });
                this.isLoading.set(false);
              }
            },
            error: (err) => {
              console.error('Error deleting contract:', err);
              this.messageService.add({
                severity: 'error',
                summary: 'Errore',
                detail: "Si è verificato un errore durante l'eliminazione del contratto",
                life: 3000,
              });
              this.isLoading.set(false);
            },
          });
        },
      );
    }
  }

  /**
   * Dopo l'eliminazione di un contratto fiduciario, riallinea lo step di registrazione:
   * se il fiduciario era avanzato oltre CONTRACT_SENT (firma / controfirma / attivazione)
   * lo riporta a CONTRACT_SENT/PENDING via l'endpoint di rollback del microservizio
   * professionals. Best-effort: la delete è già andata a buon fine, quindi in caso di
   * errore avvisiamo soltanto senza bloccare il flusso.
   */
  private restoreProfessionalContractStep(professionalId: string): void {
    const stepsToRollback = [
      RegistrationStep.CONTRACT_SIGNATURE,
      RegistrationStep.CONTRACT_COUNTERSIGNATURE,
      RegistrationStep.CONTRACT_ACTIVATED,
    ];

    this.registrationStepService.getCurrentStep(professionalId).subscribe({
      next: (current) => {
        // Già a CONTRACT_SENT/PENDING (o fase non contrattuale): nessun ripristino necessario.
        if (!current || !stepsToRollback.includes(current.step)) {
          return;
        }

        this.registrationStepService
          .rollbackToStep(
            professionalId,
            RegistrationStep.CONTRACT_SENT,
            'Contratto eliminato: ripristino a "invio contratto"',
          )
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'info',
                summary: 'Stato fiduciario ripristinato',
                detail: 'Il fiduciario è tornato in attesa di generazione contratto.',
                life: 4000,
              });
            },
            error: (err) => {
              console.error('Errore nel ripristino dello step del fiduciario dopo delete contratto:', err);
              this.messageService.add({
                severity: 'warn',
                summary: 'Attenzione',
                detail:
                  "Contratto eliminato, ma non è stato possibile ripristinare automaticamente lo stato del fiduciario.",
                life: 6000,
              });
            },
          });
      },
      error: (err) => {
        console.error('Errore nel recupero dello step corrente del fiduciario dopo delete contratto:', err);
      },
    });
  }

  onClearFilters(): void {
    // Ricarica i dati originali per ripristinare l'ordine iniziale
    this.loadContracts();
  }

  onExport(): void {
    // Usa il servizio appropriato in base al contesto
    let exportObservable: Observable<any>;

    if (this.supplierId) {
      const supplierId = Number(this.supplierId);
      exportObservable = this.supplierService.exportSuppliersRegistry(supplierId);
    } else if (this.responsibleId) {
      const responsibleId = Number(this.responsibleId);
      exportObservable = this.responsibleService.exportResponsiblesRegistry(responsibleId);
    } else if (this.subResponsibleId) {
      const subResponsibleId = Number(this.subResponsibleId);
      exportObservable = this.subResponsibleService.exportSubResponsiblesRegistry(subResponsibleId);
    } else {
      // Nessun filtro
      exportObservable = this.supplierService.exportSuppliersRegistry();
    }

    exportObservable.subscribe({
      next: (response) => this.handleExportSuccess(response),
      error: (err) => this.handleExportError(err),
    });
  }

  private handleExportSuccess(response: any): void {
    const blob = response.body as Blob | null;
    if (!blob) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore export',
        detail: 'File non ricevuto dal server',
        life: 4000,
      });
      return;
    }
    let fileName = 'Registro Contratti.xlsx';
    const contentDisposition = response.headers.get('content-disposition');
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";]+)"?/);
      if (match && match[1]) {
        fileName = decodeURIComponent(match[1]);
      }
    }
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(downloadUrl);
    this.messageService.add({
      severity: 'success',
      summary: 'Export completato',
      detail: 'Il file è stato scaricato con successo',
      life: 3000,
    });
  }

  private handleExportError(err: any): void {
    console.error("Errore durante l'export:", err);
    this.messageService.add({
      severity: 'error',
      summary: 'Errore export',
      detail: err?.error?.message || "Si è verificato un errore durante l'export",
      life: 4000,
    });
  }

  goToNuovoContratto(): void {
    // TODO: Navigate to nuovo contratto wizard
    console.log('Navigate to nuovo contratto');
    this.messageService.add({
      severity: 'info',
      summary: 'Nuovo Contratto',
      detail: 'Funzionalità in sviluppo',
      life: 3000,
    });
  }

  // Metodi di formattazione e utility rimossi - ora centralizzati in ContractRegistryService
  // Usa contractService.formatDate(), formatCurrency(), getStatusDisplay(), getStatusSeverity()
}
